import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../AppContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import Lightbox from '../components/Lightbox';
import { tr } from '../i18n';
import { ArrowLeft, ZoomIn } from 'lucide-react';
import { resolveImg, thumbImg } from '../utils/resolveImg';
import SectionTabs from '../components/SectionTabs';
import ExtrasBox from '../components/ExtrasBox';
import { getAllergens, getExtras } from '../utils/menuExtras';

const Items = () => {
  const { catSlug, subSlug } = useParams();
  const navigate = useNavigate();
  const { menu, lang, t, API, track } = useApp();
  const rawData = menu.items && menu.items[subSlug];
  const data = useMemo(
    () => rawData && { ...rawData, items: (rawData.items || []).filter((it) => it.visible !== false) },
    [rawData]
  );
  const main = menu.mainCategories.find((c) => c.slug === catSlug);
  const allSubs = useMemo(() => (menu.subCategories && menu.subCategories[catSlug]) || [], [menu.subCategories, catSlug]);
  const otherSubs = useMemo(() => allSubs.filter((s) => s.slug !== subSlug), [allSubs, subSlug]);

  const sectionLabel = tr(data?.title, lang);
  useEffect(() => {
    if (subSlug) track('section', subSlug, sectionLabel);
  }, [subSlug, sectionLabel, track]);

  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Build gallery of items that have an image
  const gallery = useMemo(() => {
    if (!data) return [];
    return (data.items || [])
      .filter((it) => !!it.image)
      .map((it) => ({ src: resolveImg(it.image, API), caption: tr(it.name, lang) }));
  }, [data, lang, API]);

  const openLightbox = (itemImage, itemName) => {
    const idx = gallery.findIndex((g) => g.src === resolveImg(itemImage, API));
    setLightboxIndex(idx >= 0 ? idx : 0);
    if (itemName) track('item', `${subSlug}:${itemName}`, itemName);
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-3xl mb-4">{t('sezioneNon')}</p>
          <button className="pill-btn" onClick={() => navigate('/')}>{t('tornaMenu')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full pt-6 pb-24 px-4 md:px-6">
      <div className="relative max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-2 mb-4">
          <button
            data-testid="items-back-btn"
            onClick={() => navigate(`/categoria/${catSlug}`)}
            className="w-11 h-11 flex-shrink-0 rounded-full border-[1.5px] border-black flex items-center justify-center hover:bg-black hover:text-white transition"
            aria-label={t('back')}
          >
            <ArrowLeft size={16} />
          </button>
          <Logo height={36} linkHome />
          <LanguageSwitcher />
        </div>

        <SectionTabs catSlug={catSlug} subs={allSubs} activeSlug={subSlug} lang={lang} />

        <div className="text-center mb-4 mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.32em] text-black/60 mb-2">{tr(main?.name, lang)}</p>
          <h1 className="font-display text-[40px] md:text-[68px] leading-[0.95] text-black">
            {tr(data.title, lang)}
          </h1>
          <div className="mt-4 hairline w-10 mx-auto" />
        </div>

        {data.note && (
          <div className="mt-4 mb-2 mx-auto max-w-xl text-center text-sm md:text-base text-black/70 italic">
            {tr(data.note, lang)}
          </div>
        )}

        <div className="mt-8">
          <ul className="divide-y divide-black/10">
            {data.items.map((it, idx) => (
              <li key={it._id || `${tr(it.name, 'it')}-${idx}`} className="py-5 md:py-6">
                <div className="flex gap-4">
                  {it.image && (
                    <button
                      type="button"
                      onClick={() => openLightbox(it.image, tr(it.name, 'it'))}
                      className="relative group w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-xl overflow-hidden border-[1.5px] border-black cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                      aria-label={lang === 'it' ? 'Ingrandisci foto' : 'Enlarge photo'}
                    >
                      <img
                        src={thumbImg(it.image, 320)}
                        alt={tr(it.name, lang)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { const f = resolveImg(it.image); if (e.currentTarget.src !== f) e.currentTarget.src = f; }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition flex items-center justify-center">
                        <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100 transition drop-shadow" />
                      </div>
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 translate="no" className="notranslate font-display text-xl md:text-2xl text-black leading-tight">
                        {tr(it.name, lang)}
                      </h3>
                      <div className="flex-1 mx-3 border-b border-dotted border-black/30 translate-y-[-4px]" />
                      <span className="font-display text-lg md:text-xl text-black whitespace-nowrap">
                        € {it.price}
                      </span>
                    </div>
                    {tr(it.desc, lang) && (
                      <p translate="no" className="notranslate mt-1.5 text-sm md:text-[15px] text-black/70 leading-snug">
                        {tr(it.desc, lang)}
                      </p>
                    )}
                    {it.allergens && (
                      <p className="mt-1 text-xs text-black/50 font-bold uppercase tracking-wider">{t('allergens')}: {it.allergens}</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {getExtras(menu).sections.includes(subSlug) && <ExtrasBox items={getExtras(menu).items} lang={lang} />}

        <div className="mt-14">
          <p className="text-center text-black/60 font-bold uppercase tracking-[0.28em] text-[10px] mb-4">{t('vediAnche')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {otherSubs.map((s) => (
              <button key={s.slug} onClick={() => navigate(`/categoria/${catSlug}/${s.slug}`)} className="pill-btn text-sm py-2 px-4">
                {tr(s.name, lang)}
              </button>
            ))}
          </div>
        </div>

        {/* Allergens legend */}
        <div className="mt-14 border-t border-black/10 pt-6">
          <p className="text-center text-black/60 font-bold uppercase tracking-[0.28em] text-[10px] mb-4">
            {t('allergensLegendTitle')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 max-w-2xl mx-auto">
            {getAllergens(menu).map((a) => (
              <div key={a.n} className="flex items-baseline gap-2 text-sm text-black/75">
                <span className="font-display text-base text-black w-5 text-right">{a.n}</span>
                <span className="font-medium">{tr(a.name, lang)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox gallery */}
      {lightboxIndex !== null && gallery.length > 0 && (
        <Lightbox
          images={gallery}
          startIndex={lightboxIndex}
          lang={lang}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
};

export default Items;
